import React, { useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import {
    Alert,
    Box,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { ACCENT, TEXT } from "../../dark-theme/palette";
import { usePagesTranslation } from "../locales";
import TutorsWorkspaceShell from "./TutorsWorkspaceShell";
import useTutorHub from "./useTutorHub";
import { TUTOR_DETAILS_KEYS, readTutorLibrary } from "./tutorHubDetails";
import { buildTutorCrossword } from "./tutorCrosswordFromText";
import { fillTutorPuzzleClues, saveTutorStoryClues } from "./tutorStoryClues";
import TutorsWorksheetPrint, { tutorPrintPageStyle } from "./TutorsWorksheetPrint";
import TutorPdfService from "../../services/TutorPdfService";
import SubscriptionService from "../../services/SubscriptionService";
import { SUBSCRIPTION_PLANS, TUTOR_EXTRA_PDF_PLAN } from "../subscription/PanelPlan";

export default function TutorsWorksheetsPage() {
    const { t } = usePagesTranslation();
    const { details, loading, error, patchDetails } = useTutorHub();
    const detailsRef = useRef(details);
    const patchRef = useRef(patchDetails);
    detailsRef.current = details;
    patchRef.current = patchDetails;
    const library = useMemo(() => readTutorLibrary(details), [details]);
    const [selectedId, setSelectedId] = useState("");
    const [printTitle, setPrintTitle] = useState("");
    const [originalOpen, setOriginalOpen] = useState(false);
    const [puzzle, setPuzzle] = useState(null);
    const [meaningsLoading, setMeaningsLoading] = useState(false);
    const [pageFormat, setPageFormat] = useState("letter");
    const [quota, setQuota] = useState(null);
    const [topUpOpen, setTopUpOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [buyingExtra, setBuyingExtra] = useState(false);
    const printRef = useRef(null);
    const orgName = String(details?.[TUTOR_DETAILS_KEYS.organizationName] || "").trim();
    const logoUrl = String(details?.[TUTOR_DETAILS_KEYS.logoUrl] || "").trim();
    const selected = library.find((item) => item.id === selectedId) || library[0] || null;
    const basePuzzle = useMemo(
        () => (selected?.sourceText ? buildTutorCrossword(selected.sourceText) : null),
        [selected?.sourceText],
    );

    useEffect(() => {
        setPrintTitle(selected?.title || "");
    }, [selected?.id, selected?.title]);

    const clueSignature = (selected?.clues || [])
        .map((row) => `${row.word}:${row.definition}`)
        .join("|");

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            if (!selected || !basePuzzle?.grid?.length) {
                setPuzzle(basePuzzle);
                setMeaningsLoading(false);
                return;
            }
            setPuzzle(basePuzzle);
            setMeaningsLoading(true);
            try {
                const result = await fillTutorPuzzleClues({
                    puzzle: basePuzzle,
                    savedClues: selected.clues,
                    title: selected.title,
                    sourceText: selected.sourceText,
                    storyId: selected.id,
                });
                if (cancelled) {
                    return;
                }
                setPuzzle(result.puzzle);
                if (result.changed) {
                    await saveTutorStoryClues(
                        detailsRef.current,
                        selected.id,
                        result.clues,
                        patchRef.current,
                    );
                }
            } finally {
                if (!cancelled) {
                    setMeaningsLoading(false);
                }
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [basePuzzle, clueSignature, selected]);

    const canPrint = Boolean(puzzle?.grid?.length);
    const hasExportCredit = Boolean(quota?.allowed);
    const worksheetTitle = printTitle.trim() || selected?.title || "";

    useEffect(() => {
        if (loading) {
            return undefined;
        }
        let cancelled = false;
        TutorPdfService.fetchQuota()
            .then((next) => {
                if (!cancelled) {
                    setQuota(next);
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [loading]);

    useEffect(() => {
        if (hasExportCredit) {
            return undefined;
        }
        const blockShortcut = (event) => {
            const key = String(event.key || "").toLowerCase();
            if ((event.ctrlKey || event.metaKey) && key === "p") {
                event.preventDefault();
                event.stopPropagation();
                setTopUpOpen(true);
            }
        };
        const originalPrint = window.print.bind(window);
        window.print = () => {
            setTopUpOpen(true);
        };
        window.addEventListener("keydown", blockShortcut, true);
        return () => {
            window.print = originalPrint;
            window.removeEventListener("keydown", blockShortcut, true);
        };
    }, [hasExportCredit]);

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: worksheetTitle || "crossword-worksheet",
        pageStyle: tutorPrintPageStyle(pageFormat),
    });

    const handleDownload = async () => {
        if (!canPrint || meaningsLoading || exporting || !hasExportCredit) {
            if (!hasExportCredit) {
                setTopUpOpen(true);
            }
            return;
        }
        setExporting(true);
        try {
            const result = await TutorPdfService.consumeExport();
            setQuota(result);
            if (!result.allowed && !result.consumed) {
                setTopUpOpen(true);
                return;
            }
            handlePrint();
        } catch (err) {
            setTopUpOpen(true);
        } finally {
            setExporting(false);
        }
    };

    const handleBuyExtra = async () => {
        setBuyingExtra(true);
        try {
            const result = await SubscriptionService.handleSubscribe(TUTOR_EXTRA_PDF_PLAN, SUBSCRIPTION_PLANS);
            if (!result.success && !result.redirecting) {
                setTopUpOpen(true);
            }
        } finally {
            setBuyingExtra(false);
        }
    };

    return (
        <TutorsWorkspaceShell
            title={t("tutors.worksheets-title", "Worksheet generator")}
            loading={loading}
            error={error}
        >
            {!hasExportCredit ? (
                <style>
                    {`@media print { html, body { visibility: hidden !important; height: 0 !important; overflow: hidden !important; } }`}
                </style>
            ) : null}
            <Typography variant="body1" sx={{ color: TEXT.secondary, lineHeight: 1.7, mb: 2 }}>
                {t(
                    "tutors.worksheets-how",
                    "1. Pick your story. 2. Set organization name and logo in Settings if you want them on the header. 3. Print / Save as PDF. Page 1 is the empty grid and clues, then the answers, and the original content is last.",
                )}
            </Typography>

            {library.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {t("tutors.worksheets-empty", "Save a crossword in Story library first, then come back to print it.")}{" "}
                    <Button component={RouterLink} to="/tutors/library" size="small" sx={{ textTransform: "none" }}>
                        {t("nav.tutors-library", "Story library")}
                    </Button>
                </Alert>
            ) : (
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 3 }}>
                    <FormControl sx={{ minWidth: 220, flex: "1 1 240px", maxWidth: 360 }}>
                        <InputLabel id="tutor-worksheet-puzzle">{t("tutors.worksheets-pick", "Crossword")}</InputLabel>
                        <Select
                            labelId="tutor-worksheet-puzzle"
                            label={t("tutors.worksheets-pick", "Crossword")}
                            value={selected?.id || selectedId}
                            onChange={(event) => setSelectedId(event.target.value)}
                        >
                            {library.map((item) => (
                                <MenuItem key={item.id} value={item.id}>
                                    {item.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label={t("tutors.worksheets-title-field", "Title")}
                        value={printTitle}
                        onChange={(event) => setPrintTitle(event.target.value)}
                        sx={{ minWidth: 200, flex: "1 1 220px", maxWidth: 360 }}
                    />
                    <Button
                        variant="outlined"
                        disabled={!selected?.sourceText}
                        onClick={() => setOriginalOpen(true)}
                        sx={{ minHeight: 44, textTransform: "none", fontWeight: 700 }}
                    >
                        {t("tutors.worksheets-original", "Original content")}
                    </Button>
                </Box>
            )}

            {selected && basePuzzle?.error === "need-words" ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {t(
                        "tutors.worksheets-need-words",
                        "Need at least four puzzle words (4–12 letters). Add WORD — meaning lines, or a longer story with more content words.",
                    )}
                </Alert>
            ) : null}

            <Card sx={{ p: 2.5, mb: 3 }}>
                <Typography variant="subtitle2" sx={{ color: TEXT.secondary, mb: 1 }}>
                    {t("tutors.worksheets-header-preview", "PDF header")}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    {logoUrl ? (
                        <Box
                            component="img"
                            src={logoUrl}
                            alt=""
                            sx={{ width: 56, height: 56, objectFit: "contain", borderRadius: 1 }}
                        />
                    ) : null}
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {worksheetTitle || orgName || t("tutors.settings-org-placeholder", "Your organization")}
                    </Typography>
                </Box>
                {!orgName || !logoUrl ? (
                    <Button component={RouterLink} to="/tutors/settings" size="small" sx={{ textTransform: "none" }}>
                        {t("tutors.worksheets-set-branding", "Set organization name and logo")}
                    </Button>
                ) : null}
            </Card>

            {canPrint || meaningsLoading ? (
                <Box sx={{ mb: 3, overflowX: "auto", maxWidth: "100%" }}>
                    <Typography variant="subtitle2" sx={{ color: TEXT.secondary, mb: 1 }}>
                        {t("tutors.worksheets-preview", "Preview (empty grid)")}
                    </Typography>
                    {meaningsLoading ? (
                        <Box
                            sx={{
                                minHeight: 220,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: `1px solid ${TEXT.secondary}`,
                                borderRadius: 1,
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {t("tutors.worksheets-processing", "Processing...")}
                            </Typography>
                        </Box>
                    ) : (
                    <Box
                        sx={{
                            maxWidth: "100%",
                            "@media (max-width: 599.95px)": { zoom: 0.72 },
                        }}
                    >
                        <TutorsWorksheetPrint
                            title={worksheetTitle}
                            orgName={orgName}
                            logoUrl={logoUrl}
                            puzzle={puzzle}
                            includeAnswerPage={false}
                            pageFormat={pageFormat}
                        />
                    </Box>
                    )}
                </Box>
            ) : null}

            <Box
                aria-hidden
                sx={{
                    position: "fixed",
                    left: 0,
                    top: 0,
                    width: 0,
                    height: 0,
                    overflow: "hidden",
                    pointerEvents: "none",
                }}
            >
                <Box ref={printRef}>
                    <TutorsWorksheetPrint
                        title={worksheetTitle}
                        orgName={orgName}
                        logoUrl={logoUrl}
                        puzzle={puzzle}
                        pageFormat={pageFormat}
                        sourceText={selected?.sourceText || ""}
                    />
                </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                <ToggleButtonGroup
                    exclusive
                    value={pageFormat}
                    onChange={(_, value) => {
                        if (value) {
                            setPageFormat(value);
                        }
                    }}
                    aria-label={t("tutors.worksheets-format", "Page size")}
                >
                    <ToggleButton value="letter" sx={{ minHeight: 44, textTransform: "none", px: 2 }}>
                        {t("tutors.worksheets-letter", "Letter")}
                    </ToggleButton>
                    <ToggleButton value="a4" sx={{ minHeight: 44, textTransform: "none", px: 2 }}>
                        {t("tutors.worksheets-a4", "A4")}
                    </ToggleButton>
                </ToggleButtonGroup>
                <Button
                    variant="contained"
                    disabled={!canPrint || meaningsLoading || exporting || !hasExportCredit}
                    onClick={handleDownload}
                    sx={{ minHeight: 44, textTransform: "none", fontWeight: 700, bgcolor: ACCENT.main }}
                >
                    {exporting
                        ? t("btn.loading", "Loading...")
                        : t("tutors.worksheets-download", "Print / Save as PDF")}
                </Button>
                {quota ? (
                    <Typography variant="body2" color="text.secondary">
                        {t("tutors.extra-pdf-quota", {
                            left: quota.monthlyLeft ?? 0,
                            extra: quota.extraPdfCredits ?? 0,
                            defaultValue: "PDF Exports: {{left}} / 50 left (+{{extra}} extra credits)",
                        })}
                    </Typography>
                ) : null}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                {t(
                    "tutors.worksheets-print-hint",
                    "In the print dialog choose Save as PDF (or your printer). Letter and A4 both fit this page. Puzzle, then answers, then the original content.",
                )}
            </Typography>

            <Dialog
                open={originalOpen}
                onClose={() => setOriginalOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {t("tutors.worksheets-original-title", "Original content")}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle2" sx={{ color: TEXT.secondary, mb: 1.5 }}>
                        {selected?.title}
                    </Typography>
                    <Typography sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
                        {selected?.sourceText || ""}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setOriginalOpen(false)}
                        sx={{ minHeight: 44, textTransform: "none" }}
                    >
                        {t("btn.close", "Close")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={topUpOpen}
                onClose={() => setTopUpOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>
                    {t("tutors.extra-pdf-modal-title", "Extra 50 PDF Exports")}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 1.5, lineHeight: 1.6 }}>
                        {quota?.reason === "subscription"
                            ? t(
                                "tutors.extra-pdf-modal-subscribe",
                                "Tutor Standard is required before extra exports can be used.",
                            )
                            : t(
                                "tutors.extra-pdf-modal-body",
                                "Your monthly PDF exports are used up. Buy 50 extra exports for $4.99.",
                            )}
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                        <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {t("tutors.extra-pdf-feature-instant", "Adds +50 PDF downloads instantly")}
                        </Typography>
                        <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {t("tutors.extra-pdf-feature-expires", "Never expires while subscription is active")}
                        </Typography>
                        <Typography component="li" variant="body2" color="text.secondary">
                            {t("tutors.extra-pdf-feature-order", "Only consumed after monthly base quota is used")}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setTopUpOpen(false)}
                        sx={{ minHeight: 44, textTransform: "none" }}
                    >
                        {t("btn.close", "Close")}
                    </Button>
                    {quota?.reason === "subscription" ? (
                        <Button
                            component={RouterLink}
                            to="/tutors/settings"
                            variant="contained"
                            sx={{ minHeight: 44, textTransform: "none", fontWeight: 700, bgcolor: ACCENT.main }}
                        >
                            {t("tutors.settings-title", "Settings")}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            disabled={buyingExtra}
                            onClick={handleBuyExtra}
                            sx={{ minHeight: 44, textTransform: "none", fontWeight: 700, bgcolor: ACCENT.main }}
                        >
                            {buyingExtra
                                ? t("subs.dialog.processing")
                                : t("tutors.extra-pdf-buy", "Buy 50 Extra Exports")}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </TutorsWorkspaceShell>
    );
}
